import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scale, Mail } from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo from "@/components/Seo";

function Section({ id, numeral, title, children }) {
  return (
    <section id={id} data-testid={`terms-section-${id}`} className="scroll-mt-28 mb-14">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
          {numeral}
        </span>
        <h2 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
          {title}
        </h2>
      </div>
      <div className="text-[15px] leading-[1.75] text-[var(--text)]/85 max-w-3xl">{children}</div>
    </section>
  );
}

const TOC = [
  ["acceptance", "§01", "Acceptance of Terms"],
  ["services", "§02", "Our Services"],
  ["scope", "§03", "Scope of Work & Engagements"],
  ["fees", "§04", "Fees, Invoicing & Payment"],
  ["cancellation", "§05", "Cancellation & Attrition"],
  ["ip", "§06", "Intellectual Property"],
  ["confidentiality", "§07", "Confidentiality"],
  ["conduct", "§08", "Acceptable Use"],
  ["third-parties", "§09", "Third-Party Vendors"],
  ["warranties", "§10", "Disclaimers & Warranties"],
  ["liability", "§11", "Limitation of Liability"],
  ["indemnity", "§12", "Indemnification"],
  ["law", "§13", "Governing Law"],
  ["updates", "§14", "Changes to These Terms"],
  ["contact", "§15", "Contact Us"],
];

export default function Terms() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Terms & Conditions"
        description="The terms governing your use of Magee Enterprises' website and event, meeting, hospitality, and B2B services."
        path="/terms"
        keywords="terms of service, terms and conditions, agreement, event services legal"
      />

      {/* HERO */}
      <section className="relative pt-36 md:pt-44 pb-14 md:pb-20 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-45 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[520px] h-[520px] -top-32 -right-32 opacity-35" />
        <HexMark size={220} opacity={0.055} className="absolute -left-10 top-24 rotate-[8deg] pointer-events-none hidden md:block" />
        <span className="corner-cross" style={{ top: 40, right: 40 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                § Legal · Terms
              </span>
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-700 border border-cyan-500/30 rounded-full px-2.5 py-1 bg-cyan-500/5 inline-flex items-center gap-1.5">
                <Scale size={11} /> Effective · February 2026
              </span>
            </div>
            <MaskedReveal
              lines={["Terms &", "conditions."]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[13vw] md:text-[8vw] lg:text-[7vw]"
              stagger={0.09}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                These Terms govern your use of the Magee Enterprises website and any services we deliver on your behalf. By engaging with us, you agree to these terms.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="terms-hero-cta">
                  Ask a question <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern-dense opacity-30 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* ToC */}
            <aside className="col-span-12 lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-4 hidden lg:block">
                  Contents
                </div>
                <nav data-testid="terms-toc" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-1 gap-2">
                  {TOC.map(([id, ref, label]) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className="group flex items-center gap-3 rounded-lg px-3 py-2 border border-transparent hover:border-[var(--border)] hover:bg-white/60 transition-all"
                    >
                      <span className="font-mono-brand text-[10px] tracking-[0.22em] text-[var(--text-muted)] group-hover:text-[var(--accent)]">
                        {ref}
                      </span>
                      <span className="text-[13px] text-[var(--text)]/75 group-hover:text-[var(--text)] leading-tight">
                        {label}
                      </span>
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="col-span-12 lg:col-span-9">
              <p className="text-lg leading-relaxed text-[var(--text)]/80 max-w-3xl">
                Welcome to Magee Enterprises (&ldquo;<span className="text-[var(--text)] font-medium">Magee</span>,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). These Terms &amp; Conditions (&ldquo;Terms&rdquo;) form a legally binding agreement between you and Magee governing your use of <span className="text-[var(--text)] font-medium">mageeenterprises.com</span> and any event, meeting, hospitality, travel, project management, or B2B consulting services we provide. Please read them carefully before booking a consultation, submitting a proposal request, or engaging our team.
              </p>

              <div className="mt-14">
                <Section id="acceptance" numeral="§01" title="Acceptance of Terms">
                  <p>
                    By accessing this website, submitting a proposal request, or signing a Statement of Work (&ldquo;SOW&rdquo;) with us, you accept these Terms in full. If you do not agree, please do not use our website or engage our services. Individual engagements may be governed by an additional signed agreement, which will control in the event of a conflict.
                  </p>
                </Section>

                <Section id="services" numeral="§02" title="Our Services">
                  <p>
                    Magee provides full-service event and meeting management, event technology solutions, VIP and film production, site selection and sourcing, hotel and travel accommodations, logistics planning, project management, and select B2B consulting services (formation, trademark, brand launch, and operations support). Specific deliverables, timelines, and pricing are defined in each SOW or engagement letter.
                  </p>
                </Section>

                <Section id="scope" numeral="§03" title="Scope of Work & Engagements">
                  <p>
                    Every engagement is scoped in writing. Work outside the agreed scope is treated as a change order and requires written approval before execution. We reserve the right to decline engagements that fall outside our practice areas, present conflicts of interest, or cannot be delivered to our quality standards.
                  </p>
                </Section>

                <Section id="fees" numeral="§04" title="Fees, Invoicing & Payment">
                  <p>
                    Fees are set in your SOW and may be structured as fixed-fee, hourly, retained, or performance-based. Unless otherwise stated: (a) invoices are due Net 15 from date of issue; (b) late balances accrue interest at 1.5% per month or the maximum permitted by law; (c) travel, per diem, and third-party pass-through costs are billed at cost and are not included in service fees; and (d) taxes and government fees are the client&rsquo;s responsibility.
                  </p>
                </Section>

                <Section id="cancellation" numeral="§05" title="Cancellation & Attrition">
                  <p>
                    Event programs are governed by the cancellation, attrition, and force-majeure terms contained in each vendor and venue contract, as reflected in your SOW. If you cancel an engagement, we will bill for all work completed and non-recoverable third-party commitments up to the cancellation date. Where a vendor or venue applies attrition or cancellation charges, those charges pass through to the client.
                  </p>
                </Section>

                <Section id="ip" numeral="§06" title="Intellectual Property">
                  <p>
                    All content, code, designs, methodologies, playbooks, and materials created by Magee remain our property unless expressly assigned to you in writing. Upon full payment of fees, you receive a non-exclusive, perpetual license to use the specific deliverables produced under your SOW for the purpose for which they were commissioned. Trademarks, service marks, logos, and brand assets displayed on this website are the property of their respective owners.
                  </p>
                </Section>

                <Section id="confidentiality" numeral="§07" title="Confidentiality">
                  <p>
                    We treat all client information as confidential. Confidential information disclosed by either party will be used solely to deliver the engaged services and will not be shared with third parties except as needed to perform the work (for example, vendors and venues), as required by law, or with the disclosing party&rsquo;s written consent. These obligations survive termination of the engagement.
                  </p>
                </Section>

                <Section id="conduct" numeral="§08" title="Acceptable Use of the Website">
                  <p>
                    You agree not to (a) reverse engineer, scrape, or copy substantial portions of the website; (b) attempt to gain unauthorized access to our systems; (c) misrepresent your identity when submitting proposal requests; (d) upload malicious code or content; or (e) use the website for any unlawful purpose. We reserve the right to block access for violations of this section.
                  </p>
                </Section>

                <Section id="third-parties" numeral="§09" title="Third-Party Vendors & Services">
                  <p>
                    Delivering events often requires third-party vendors (hotels, airlines, A/V, catering, staffing, technology providers, etc.). While we vet vendors carefully, we are not responsible for their acts or omissions except where expressly stated in your SOW. Third-party services referenced on this site (including TidyCal, SendLayer, and payment processors) are governed by their own terms and privacy policies.
                  </p>
                </Section>

                <Section id="warranties" numeral="§10" title="Disclaimers & Warranties">
                  <p>
                    This website and its content are provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; While we work to ensure information is accurate, we make no warranties, express or implied, regarding the completeness, reliability, or availability of the website or its content. Services are delivered under the specific warranties set out in your SOW. Except as expressly stated, all other warranties are disclaimed to the fullest extent permitted by law.
                  </p>
                </Section>

                <Section id="liability" numeral="§11" title="Limitation of Liability">
                  <p>
                    To the fullest extent permitted by law, Magee&rsquo;s aggregate liability for any claim arising out of or related to these Terms or the services shall not exceed the total fees paid by you to Magee under the specific SOW giving rise to the claim in the twelve (12) months preceding the event. In no event will Magee be liable for indirect, incidental, special, consequential, or punitive damages, including lost profits or revenues.
                  </p>
                </Section>

                <Section id="indemnity" numeral="§12" title="Indemnification">
                  <p>
                    Each party agrees to defend, indemnify, and hold the other harmless from third-party claims arising out of the indemnifying party&rsquo;s (a) breach of these Terms or the applicable SOW; (b) negligence or willful misconduct; or (c) violation of applicable law. This obligation survives termination of the engagement.
                  </p>
                </Section>

                <Section id="law" numeral="§13" title="Governing Law & Dispute Resolution">
                  <p>
                    These Terms are governed by the laws of the State of New Jersey, without regard to conflict-of-law principles. Any dispute arising out of or related to these Terms will first be addressed through good-faith negotiation. If unresolved within 30 days, disputes shall be submitted to binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules, with venue in Essex County, New Jersey. Judgment on the arbitration award may be entered in any court of competent jurisdiction.
                  </p>
                </Section>

                <Section id="updates" numeral="§14" title="Changes to These Terms">
                  <p>
                    We may update these Terms from time to time. The &ldquo;Effective&rdquo; date at the top of the page will be revised accordingly. Continued use of the website or services after any update constitutes acceptance of the revised Terms.
                  </p>
                </Section>

                <Section id="contact" numeral="§15" title="Contact Us">
                  <div className="rounded-2xl border border-[var(--border)] bg-white p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shrink-0 shadow-lg">
                      <Mail size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-editorial text-lg md:text-xl leading-tight">Magee Enterprises · Legal</div>
                      <div className="mt-2 text-[14.5px] text-[var(--text-muted)] leading-relaxed">
                        <a href="mailto:dion@mageeenterprises.com" className="text-[var(--accent)] underline underline-offset-4 hover:no-underline">dion@mageeenterprises.com</a>
                      </div>
                    </div>
                  </div>
                </Section>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
